import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Fade,
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { InputBase } from "@mui/material";
import {
  BORDER,
  TEXT_MUTED,
  TEXT_SUBTLE,
  TEXT_HEADING,
  BRAND_BLUE,
  BRAND_BLUE_SOFT,
  CARD_SHADOW,
  AVATAR_PALETTE,
  formatYTick,
  niceCeil,
} from "./designTokens";
import html2canvas from "html2canvas";

/* ---------------------------------------------------------------------------
 * BreakdownModal — the shell every breakdown screen renders inside.
 * Handles: overlay, header (title/subtitle/last-updated/download/pin/close),
 * optional filter bar, and a scrollable body where callers stack cards.
 * ------------------------------------------------------------------------- */
export function BreakdownModal({
  open,
  onClose,
  title,
  subtitle,
  lastUpdated,
  pinned,
  onTogglePin,
  onDownload, // now optional — falls back to built-in capture if omitted
  showPin = true,
  filters,
  children,
  maxWidth = 720,
}) {
  const captureRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const slugify = (s) =>
    (s || "breakdown")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

const handleDownload = async () => {
  if (onDownload) {
    onDownload();
    return;
  }
  const el = captureRef.current;
  if (!el || downloading) return;

  setDownloading(true);

  // Temporarily remove the scroll clipping so html2canvas captures the
  // full content height, not just what's currently visible in the viewport.
  const prevOverflow = el.style.overflowY;
  const prevHeight = el.style.height;
  const prevMaxHeight = el.style.maxHeight;

  el.style.overflowY = "visible";
  el.style.height = "auto";
  el.style.maxHeight = "none";

  try {
    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      height: el.scrollHeight,
      windowHeight: el.scrollHeight,
    });

    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${slugify(title)}-${Date.now()}.jpeg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    console.error("Failed to download breakdown as JPEG:", err);
  } finally {
    // restore scroll behavior regardless of success/failure
    el.style.overflowY = prevOverflow;
    el.style.height = prevHeight;
    el.style.maxHeight = prevMaxHeight;
    setDownloading(false);
  }
};

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(92vw, " + maxWidth + "px)",
            maxHeight: "88vh",
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: "0 24px 48px -12px rgba(15,23,42,0.28)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header — kept outside captureRef so Download/Pin/Close buttons
              never end up baked into the exported image */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              px: 3,
              pt: 2.5,
              pb: 2,
              borderBottom: BORDER,
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: TEXT_HEADING }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED, mt: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {lastUpdated && (
                <Box sx={{ textAlign: "right", mr: 0.5 }}>
                  <Typography sx={{ fontSize: 10, color: TEXT_SUBTLE, fontWeight: 700 }}>
                    LAST UPDATED
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
                    {lastUpdated}
                  </Typography>
                </Box>
              )}

              <Button
                size="small"
                onClick={handleDownload}
                disabled={downloading}
                startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: TEXT_HEADING,
                  border: BORDER,
                  borderRadius: 1.5,
                  px: 1.5,
                }}
              >
                {downloading ? "Downloading…" : "Download"}
              </Button>

              {showPin && (
                <Button
                  size="small"
                  onClick={onTogglePin}
                  startIcon={
                    pinned ? (
                      <PushPinIcon sx={{ fontSize: 16, color: BRAND_BLUE }} />
                    ) : (
                      <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: pinned ? BRAND_BLUE : TEXT_HEADING,
                    border: BORDER,
                    borderRadius: 1.5,
                    px: 1.5,
                  }}
                >
                  Pin
                </Button>
              )}

              <IconButton size="small" onClick={onClose}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {filters && <Box sx={{ px: 3, pt: 2, flexShrink: 0 }}>{filters}</Box>}

          {/* Scrollable body — this is what gets captured */}
          <Box
            ref={captureRef}
            sx={{
              px: 3,
              py: 2.5,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              bgcolor: "#fff", // ensure captured canvas isn't transparent
            }}
          >
            {children}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

/* ---------------------------------------------------------------------------
 * FilterBar — row of dropdown filters + Reset / Apply
 * ------------------------------------------------------------------------- */
export function FilterBar({
  filters = [],
  onReset,
  onApply,
  resetLabel = "Reset",
  applyLabel = "Apply",
}) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}
    >
      {/* ...unchanged select rendering... */}
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={onReset}
        sx={{
          textTransform: "none",
          fontSize: 12.5,
          fontWeight: 600,
          color: TEXT_MUTED,
        }}
      >
        {resetLabel}
      </Button>
      <Button
        onClick={onApply}
        variant="contained"
        sx={{
          textTransform: "none",
          fontSize: 12.5,
          fontWeight: 600,
          bgcolor: BRAND_BLUE,
          borderRadius: 1.5,
          boxShadow: "none",
          "&:hover": { bgcolor: "#0D47A1", boxShadow: "none" },
        }}
      >
        {applyLabel}
      </Button>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * DonutBreakdownCard — donut + legend list with a Pie/Table view toggle,
 * a highlighted-segment callout, and a totals footer row.
 * segments: [{ label, value, color }]
 * ------------------------------------------------------------------------- */
export function DonutBreakdownCard({
  title,
  description,
  segments = [],
  centerLabel,
  footerLeft,
  footerRight,
  size = 170,
  view: controlledView,    
  onViewChange,
  loading = false,              
}) {
  const [internalView, setInternalView] = useState("pie");
  const view = controlledView ?? internalView; // controlled if provided, else self-managed
  const [hoverIdx, setHoverIdx] = useState(null);

  const setView = (v) => {
    setInternalView(v); // keep working uncontrolled if no onViewChange is passed
    onViewChange?.(v);
  };


  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const strokeWidth = size * (20 / 150);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let cumulative = 0;
  const activeIdx = hoverIdx;
  const active = activeIdx != null ? segments[activeIdx] : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        border: BORDER,
        borderRadius: 2.5,
        boxShadow: CARD_SHADOW,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 0.5,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}
          >
            {title}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>

        <ButtonGroup size="small" sx={{ boxShadow: "none" }}>
          {["Pie", "Table"].map((label) => {
            const key = label.toLowerCase();
            return (
              <Button
                key={key}
                onClick={() => setView(key)}
                sx={{
                  textTransform: "none",
                  fontSize: 11.5,
                  fontWeight: 600,
                  px: 1.5,
                  color: view === key ? "#fff" : TEXT_MUTED,
                  bgcolor: view === key ? BRAND_BLUE : "transparent",
                  border: BORDER,
                  "&:hover": { bgcolor: view === key ? BRAND_BLUE : "#F1F5F9" },
                }}
              >
                {label}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>
     
       {loading ? ( // add — skeleton block replaces the whole pie/table area
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: 3, mt: 2 }}>
          <Skeleton variant="circular" width={size} height={size} sx={{ flexShrink: 0 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, width: "100%" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, py: 0.5 }}>
                <Skeleton variant="text" width="55%" height={16} />
                <Skeleton variant="text" width="15%" height={16} />
              </Box>
            ))}
          </Box>
        </Box>
      ) : view === "pie" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 3,
            mt: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: size,
              height: size,
              flexShrink: 0,
            }}
          >
          <div style={{display:'flex',flexDirection:'column',justifyContent:"center",alignItems:'center',gap:'20px'}}>
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                {segments.map((seg, i) => {
                  const fraction = seg.value / total;
                  const dash = fraction * circumference;
                  const gap = circumference - dash;
                  const offset = -cumulative * circumference;
                  cumulative += fraction;
                  return (
                    <circle
                      key={seg.label}
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke={seg.color}
                      strokeWidth={strokeWidth}
                      strokeDasharray={`${dash} ${gap}`}
                      strokeDashoffset={offset}
                      opacity={activeIdx == null || activeIdx === i ? 1 : 0.35}
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(null)}
                      style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                    />
                  );
                })}
              </g>
            </svg>
          </div>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{ fontSize: 22, fontWeight: 700, color: TEXT_HEADING }}
              >
                {active
                  ? active.value.toLocaleString()
                  : (centerLabel?.value ?? total.toLocaleString())}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: TEXT_SUBTLE,
                  textAlign: "center",
                  lineHeight: 1.3,
                  px: 1,
                }}
              >
                {active ? active.label : (centerLabel?.label ?? "TOTAL")}
              </Typography>
            </Box>
          </Box>
     

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              width: "100%",
            }}
          >
            {segments.map((seg, i) => (
              <Box
                key={seg.label}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: "pointer",
                  bgcolor: activeIdx === i ? "#EFF6FF" : "transparent",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: seg.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: 12.5, color: TEXT_HEADING }}>
                    {seg.label}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 12.5, fontWeight: 700, color: TEXT_HEADING }}
                >
                  {seg.value.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
   

        </Box>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          {segments.map((seg) => (
            <Box
              key={seg.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                borderBottom: "1px solid #F1F5F9",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: seg.color,
                  }}
                />
                <Typography sx={{ fontSize: 12.5, color: TEXT_HEADING }}>
                  {seg.label}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 700, color: TEXT_HEADING }}
              >
                {seg.value.toLocaleString()} ·{" "}
                {((seg.value / total) * 100).toFixed(1)}%
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {(footerLeft || footerRight) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 1.5,
            pt: 1.5,
            borderTop: BORDER,
          }}
        >
          <Typography sx={{ fontSize: 11.5, color: TEXT_SUBTLE }}>
            {footerLeft}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: TEXT_SUBTLE }}>
            {footerRight}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/* ---------------------------------------------------------------------------
 * SearchableDataTableCard — search box + sortable numeric columns + a
 * totals footer row (instead of pagination).
 * columns: [{ key, label, sortable?, align?, render?(row) }]
 * ------------------------------------------------------------------------- */
export function SearchableDataTableCard({
  title,
  description,
  columns = [],
  rows = [],
  searchKey,
  searchPlaceholder = "Search...",
  footerLeft,
  footerRight,
  loading = false, 
  skeletonRowCount = 6,
  sortKey: controlledSortKey, 
  sortOrder: controlledSortOrder,
  onSortChange,
    query: controlledQuery,    
  onQueryChange,    
}) {
  const [internalSortKey, setInternalSortKey] = useState(null);
  const [internalSortDir, setInternalSortDir] = useState("asc");
const [internalQuery, setInternalQuery] = useState("");
  const isControlled = typeof onSortChange === "function";
  const sortKey = isControlled ? controlledSortKey : internalSortKey;
  const sortDir = isControlled ? controlledSortOrder : internalSortDir;

   const isQueryControlled = typeof onQueryChange === "function"; 
  const query = isQueryControlled ? controlledQuery : internalQuery; 
  const setQuery = isQueryControlled ? onQueryChange : setInternalQuery;



  const filtered =
    !isQueryControlled && searchKey
      ? rows.filter((r) =>
          String(r[searchKey] ?? "").toLowerCase().includes(query.toLowerCase()),
        )
      : rows;

  const sorted =
    !isControlled && sortKey
      ? [...filtered].sort((a, b) => {
          const av = a[sortKey], bv = b[sortKey];
          const cmp =
            typeof av === "number" && typeof bv === "number"
              ? av - bv
              : String(av).localeCompare(String(bv));
          return sortDir === "asc" ? cmp : -cmp;
        })
      : filtered;

  const toggleSort = (key) => {
    if (loading) return;
    if (isControlled) {
      // three-state cycle, same as DataTableCard's handleSortClick
      if (sortKey !== key) {
        onSortChange(key, "asc");
      } else if (sortDir === "asc") {
        onSortChange(key, "desc");
      } else {
        onSortChange("", "");
      }
      return;
    }
    if (internalSortKey === key) {
      setInternalSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setInternalSortKey(key);
      setInternalSortDir("asc");
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, border: BORDER, borderRadius: 2.5, boxShadow: CARD_SHADOW }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
        }}
      >
        <Box>
          {title && (
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>

        {searchKey && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              border: BORDER,
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
              minWidth: 160,
            }}
          >
            <SearchIcon sx={{ fontSize: 15, color: TEXT_SUBTLE }} />
            <InputBase
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ fontSize: 12.5, flex: 1 }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "auto",
            "& td, & th": { whiteSpace: "nowrap" },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                 onClick={c.sortable && !loading ? () => toggleSort(c.key) : undefined}
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: TEXT_SUBTLE,
                    textTransform: "uppercase",
                    border: "none",
                    pb: 1,
                    cursor: c.sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                    }}
                  >
                    {c.label}
                    {c.sortable &&
                      (sortKey === c.key ? (
                        sortDir === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        )
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                      ))}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? ( // add — skeleton rows
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} sx={{ borderColor: "#F1F5F9" }}>
                      <Skeleton variant="text" width={c.align === "left" ? "70%" : "50%"} height={16} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              sorted.map((row, i) => (
                <TableRow key={row.id ?? i} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} sx={{ fontSize: 12.5, color: "#334155", borderColor: "#F1F5F9" }}>
                      {c.render ? c.render(row, i) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {(footerLeft || footerRight) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 1.5,
            pt: 1,
            borderTop: BORDER,
          }}
        >
          <Typography sx={{ fontSize: 11.5, color: TEXT_SUBTLE }}>
            {footerLeft}
          </Typography>
          <Typography
            sx={{ fontSize: 11.5, fontWeight: 700, color: "#DC2626" }}
          >
            {footerRight}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
/* ---------------------------------------------------------------------------
 * StatCardsRow — the row of small metric cards under the header
 * stats: [{ icon, iconBg, iconColor, label, value, trend?: { direction, label, color } }]
 * ------------------------------------------------------------------------- */
export function StatCardsRow({ stats = [] }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
      {stats.map((s, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            flex: "1 1 140px",
            minWidth: 130,
            p: 1.75,
            border: BORDER,
            borderRadius: 2,
            boxShadow: CARD_SHADOW,
          }}
        >
          {s.icon && (
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: 1.25,
                bgcolor: s.iconBg || BRAND_BLUE_SOFT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 0.75,
              }}
            >
              <s.icon sx={{ fontSize: 14, color: s.iconColor || BRAND_BLUE }} />
            </Box>
          )}
          <Typography sx={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600 }}>
            {s.label}
          </Typography>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: TEXT_HEADING,
              mt: 0.25,
            }}
          >
            {s.value}
          </Typography>
          {s.subtext && (
            <Typography
              sx={{
                fontSize: 12,
                color: s.subtextColor || TEXT_SUBTLE,
                fontWeight: s.subtextColor ? 700 : 400,
                mt: 0.25,
              }}
            >
              {s.subtext}
            </Typography>
          )}
          {s.trend && (
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                mt: 0.25,
                color: s.trend.direction === "up" ? "#16A34A" : "#DC2626",
              }}
            >
              {s.trend.direction === "up" ? "▲" : "▼"} {s.trend.label}
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * useMeasuredSize — resize-aware container hook (shared by the chart)
 * ------------------------------------------------------------------------- */
function useMeasuredSize() {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect || {};
      if (width && height) setSize({ width, height });
    });
    ro.observe(el);
    setSize({ width: el.clientWidth, height: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  return [containerRef, size];
}

/* ---------------------------------------------------------------------------
 * TrendLineChart — line/area chart with optional dashed target line and
 * a hover tooltip on the highlighted point. Used inside TrendChartCard.
 * ------------------------------------------------------------------------- */
function TrendLineChart({
  points = [],
  labels = [],
  color = BRAND_BLUE,
  area = false,
  targetValue = null,
  targetLabel = "",
  isCurrency = false,
  isPercent = false,
  height = 220,
}) {
  const [containerRef, { width, height: h }] = useMeasuredSize();
  const [hoverIdx, setHoverIdx] = useState(null);

  if (width === 0 || h === 0 || points.length === 0) {
    return <Box ref={containerRef} sx={{ width: "100%", height }} />;
  }

  const PAD_L = 44,
    PAD_R = 16,
    PAD_T = 16,
    PAD_B = 26;
  const plotW = width - PAD_L - PAD_R;
  const plotH = h - PAD_T - PAD_B;

  const dataMax = Math.max(...points, targetValue || 0);
  const max = niceCeil(dataMax * 1.05);
  const min = 0;
  const range = max - min || 1;

  const xFor = (i) => PAD_L + (i / (points.length - 1)) * plotW;
  const yFor = (v) => PAD_T + plotH - ((v - min) / range) * plotH;

  const linePath = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${xFor(i).toFixed(1)},${yFor(p).toFixed(1)}`,
    )
    .join(" ");
  const areaPath = area
    ? `${linePath} L${xFor(points.length - 1).toFixed(1)},${yFor(min)} L${xFor(0)},${yFor(min)} Z`
    : "";

  const yTicks = Array.from({ length: 5 }, (_, i) => (max / 4) * i).reverse();

  const xLabelIdx = (() => {
    if (labels.length === 0) return [];
    const count = Math.min(6, labels.length);
    const step = (labels.length - 1) / (count - 1 || 1);
    return Array.from({ length: count }, (_, i) => Math.round(i * step));
  })();

  const fmt = (v) =>
    isCurrency
      ? formatYTick(v)
      : isPercent
        ? `${Math.round(v)}%`
        : Math.round(v);
  const activeIdx = hoverIdx ?? points.length - 1;

  return (
    <Box ref={containerRef} sx={{ width: "100%", height }}>
      <svg
        width={width}
        height={h}
        viewBox={`0 0 ${width} ${h}`}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={width - PAD_R}
              y1={yFor(t)}
              y2={yFor(t)}
              stroke="#EEF2F6"
              strokeWidth={1}
            />
            <text
              x={PAD_L - 8}
              y={yFor(t) + 3}
              textAnchor="end"
              fontSize={9}
              fill={TEXT_SUBTLE}
            >
              {fmt(t)}
            </text>
          </g>
        ))}

        {targetValue != null && (
          <>
            <line
              x1={PAD_L}
              x2={width - PAD_R}
              y1={yFor(targetValue)}
              y2={yFor(targetValue)}
              stroke="#93C5FD"
              strokeDasharray="4 4"
              strokeWidth={1.5}
            />
            {targetLabel && (
              <text
                x={width - PAD_R}
                y={yFor(targetValue) - 5}
                textAnchor="end"
                fontSize={9}
                fill="#60A5FA"
              >
                {targetLabel}
              </text>
            )}
          </>
        )}

        {xLabelIdx.map((idx, i) => (
          <text
            key={idx}
            x={xFor(idx)}
            y={h - 8}
            textAnchor={
              i === 0 ? "start" : i === xLabelIdx.length - 1 ? "end" : "middle"
            }
            fontSize={9}
            fill={TEXT_SUBTLE}
          >
            {labels[idx]}
          </text>
        ))}

        {area && (
          <path d={areaPath} fill={color} opacity={0.08} stroke="none" />
        )}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((p, i) => (
          <circle
            key={i}
            cx={xFor(i)}
            cy={yFor(p)}
            r={i === activeIdx ? 4 : 3}
            fill={color}
            stroke="#fff"
            strokeWidth={1.5}
            onMouseEnter={() => setHoverIdx(i)}
            style={{ cursor: "pointer" }}
          />
        ))}

        {hoverIdx != null && (
          <g>
            <line
              x1={xFor(hoverIdx)}
              x2={xFor(hoverIdx)}
              y1={PAD_T}
              y2={h - PAD_B}
              stroke="#CBD5E1"
              strokeDasharray="3 3"
            />
            <rect
              x={Math.min(
                Math.max(xFor(hoverIdx) - 45, PAD_L),
                width - PAD_R - 90,
              )}
              y={yFor(points[hoverIdx]) - 32}
              width={90}
              height={22}
              rx={4}
              fill="#0F172A"
            />
            <text
              x={Math.min(
                Math.max(xFor(hoverIdx), PAD_L + 45),
                width - PAD_R - 45,
              )}
              y={yFor(points[hoverIdx]) - 17}
              textAnchor="middle"
              fontSize={10}
              fill="#fff"
            >
              {labels[hoverIdx]} · {fmt(points[hoverIdx])}
            </text>
          </g>
        )}
      </svg>
    </Box>
  );
}
/* ---------------------------------------------------------------------------
 * TrendTableCard — period toggle (Daily/Weekly/Monthly) + a dated data table.
 * Used where a "trend over time" section is tabular instead of a line chart.
 * ------------------------------------------------------------------------- */
export function TrendTableCard({
  title,
  description,
  columns = [],
  rows = [],
  periods = ["Daily", "Weekly", "Monthly"],
  defaultPeriod = "Daily",
  onPeriodChange,
  loading = false,
  skeletonRowCount = 5,
  sortKey = "",        
  sortOrder = "",      
  onSortChange,         
}) {
  const [period, setPeriod] = useState(defaultPeriod);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    onPeriodChange?.(p);
  };

  const handleSortClick = (key) => {
    if (loading || !onSortChange) return;
    if (sortKey !== key) {
      onSortChange(key, "asc");
    } else if (sortOrder === "asc") {
      onSortChange(key, "desc");
    } else if (sortOrder === "desc") {
      onSortChange("", "");
    } else {
      onSortChange(key, "asc");
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.25, border: BORDER, borderRadius: 2.5, boxShadow: CARD_SHADOW }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: description ? 0.5 : 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}>{title}</Typography>

        {periods?.length > 0 && (
          <ButtonGroup size="small" sx={{ boxShadow: "none" }}>
            {periods.map((p) => (
              <Button
                key={p}
                onClick={() => handlePeriodChange(p)}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: 11.5,
                  fontWeight: 600,
                  px: 1.25,
                  color: period === p ? TEXT_HEADING : TEXT_MUTED,
                  bgcolor: period === p ? "#F1F5F9" : "transparent",
                  border: BORDER,
                  "&:hover": { bgcolor: "#F1F5F9" },
                }}
              >
                {p}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Box>

      {description && <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mb: 1.5 }}>{description}</Typography>}

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ width: "100%", tableLayout: "auto", "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                  onClick={c.sortable ? () => handleSortClick(c.key) : undefined} 
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: TEXT_SUBTLE,
                    textTransform: "uppercase",
                    border: "none",
                    pb: 1,
                    cursor: c.sortable ? "pointer" : "default", 
                    userSelect: "none", 
                  }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                    {c.label}
                    {c.sortable && ( 
                      sortKey === c.key ? (
                        sortOrder === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ) : sortOrder === "desc" ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                        )
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                      )
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} sx={{ borderColor: "#F1F5F9" }}>
                      <Skeleton variant="text" width={c.align === "left" ? "70%" : "50%"} height={16} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              rows.map((row, i) => (
                <TableRow key={row.id ?? i} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} sx={{ fontSize: 12.5, color: "#334155", borderColor: "#F1F5F9" }}>
                      {c.render ? c.render(row, i) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
/* ---------------------------------------------------------------------------
 * TrendChartCard — the white card wrapper: title, description, period
 * toggle (Daily/Weekly/Monthly) and the chart itself.
 * ------------------------------------------------------------------------- */
export function TrendChartCard({
  title,
  description,
  chart, // { points, labels, color, area?, targetValue?, targetLabel?, isCurrency?, isPercent? }
  periods = ["Daily", "Weekly", "Monthly"],
  defaultPeriod = "Daily",
}) {
  const [period, setPeriod] = useState(defaultPeriod);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        border: BORDER,
        borderRadius: 2.5,
        boxShadow: CARD_SHADOW,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: description ? 0.5 : 1.5,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}>
          {title}
        </Typography>

        {periods?.length > 0 && (
          <ButtonGroup size="small" sx={{ boxShadow: "none" }}>
            {periods.map((p) => (
              <Button
                key={p}
                onClick={() => setPeriod(p)}
                sx={{
                  textTransform: "none",
                  fontSize: 11.5,
                  fontWeight: 600,
                  px: 1.25,
                  color: period === p ? TEXT_HEADING : TEXT_MUTED,
                  bgcolor: period === p ? "#F1F5F9" : "transparent",
                  border: BORDER,
                  "&:hover": { bgcolor: "#F1F5F9" },
                }}
              >
                {p}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Box>

      {description && (
        <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mb: 1.5 }}>
          {description}
        </Typography>
      )}

      <TrendLineChart {...chart} />
    </Paper>
  );
}

/* ---------------------------------------------------------------------------
 * RankedBarList — horizontal ranked bars, e.g. "Top 10 Most Common DRGs"
 * items: [{ rank, label, value, displayValue }]
 * ------------------------------------------------------------------------- */
export function RankedBarList({
  title,
  description,
  items = [],
  barColor = BRAND_BLUE,
  footer,
}) {
  const max = Math.max(...items.map((i) => i.value), 1);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        border: BORDER,
        borderRadius: 2.5,
        boxShadow: CARD_SHADOW,
      }}
    >
      {title && (
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mt: 0.25, mb: 1.5 }}>
          {description}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          mt: title ? 1.5 : 0,
        }}
      >
        {items.map((item) => (
          <Box
            key={item.rank}
            sx={{
              display: "grid",
              gridTemplateColumns: "16px 1fr auto",
              alignItems: "center",
              columnGap: 1.25,
            }}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}
            >
              {item.rank}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
              <Typography sx={{ fontSize: 11.5, color: "#475569" }}>
                {item.label}
              </Typography>
              <Box
                sx={{
                  height: 8,
                  bgcolor: "#E2E8F0",
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${(item.value / max) * 100}%`,
                    height: "100%",
                    bgcolor: barColor,
                    borderRadius: 10,
                  }}
                />
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#334155",
                textAlign: "right",
              }}
            >
              {item.displayValue ?? item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {footer}
    </Paper>
  );
}

/* ---------------------------------------------------------------------------
 * DataTableCard — generic metrics table.
 * columns: [{ key, label, align?, render?(row) }]
 * rows: array of plain objects
 * ------------------------------------------------------------------------- */
export function DataTableCard({
  title,
  description,
  columns = [],
  rows = [],
  pagination,
  action,
  footer,
  sortKey,    
  sortOrder,   
  onSortChange,
  loading = false,  
  skeletonRowCount = 5,
}) {
    const handleSortClick = (key) => {
    if (loading || !onSortChange) return;
    if (!onSortChange) return;
    // three-state cycle per column: neutral -> asc -> desc -> neutral
    if (sortKey !== key) {
      onSortChange(key, "asc");
    } else if (sortOrder === "asc") {
      onSortChange(key, "desc");
    } else if (sortOrder === "desc") {
      onSortChange("", ""); // back to neutral
    } else {
      onSortChange(key, "asc");
    }
  };
  return (
    <Paper elevation={0} sx={{ p: 2, border: BORDER, borderRadius: 2.5, boxShadow: CARD_SHADOW }}>
      {(title || action) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.25 }}>
          <Box>
            {title && (
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}>
                {title}
              </Typography>
            )}
            {description && (
              <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mt: 0.25 }}>
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ mt: 1.5, width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ width: "100%", tableLayout: "auto", "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead sx={{ width: "100%" }}>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                  onClick={c.sortable ? () => handleSortClick(c.key) : undefined} // add
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: TEXT_SUBTLE,
                    textTransform: "uppercase",
                    border: "none",
                    pb: 1,
                    cursor: c.sortable ? "pointer" : "default", // add
                    userSelect: "none", // add
                  }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}> {/* add wrapper */}
                    {c.label}
                    {c.sortable && ( // add sort icon
                      sortKey === c.key ? (
                        sortOrder === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ) : sortOrder === "desc" ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                        )
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                      )
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
         <TableBody>
            {loading ? ( 
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} sx={{ borderColor: "#F1F5F9" }}>
                      <Skeleton variant="text" width={c.align === "left" ? "70%" : "50%"} height={16} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              rows.map((row, i) => (
                <TableRow key={row.id ?? i} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} sx={{ fontSize: 12.5, color: "#334155", borderColor: "#F1F5F9" }}>
                      {c.render ? c.render(row, i) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {pagination && (
        <Typography sx={{ fontSize: 11, color: TEXT_SUBTLE, textAlign: "right", mt: 1 }}>
          {pagination}
        </Typography>
      )}
      {footer}
    </Paper>
  );
}
/* ---------------------------------------------------------------------------
 * SpecialtyBreakdownCard — Pie/Table toggle where Table mode is a full
 * searchable, sortable table with a computed "% of Total" column and a
 * totals footer row. Pie mode reuses the existing donut rendering.
 * segments: [{ label, value, color }]
 * ------------------------------------------------------------------------- */
export function SpecialtyBreakdownCard({
  title,
  description,
  segments = [],
  centerLabel,
  footerLeft,   
  footerRight,
  size = 170,
  view: controlledView,
  onViewChange,
  loading = false,
  skeletonRowCount = 8,
  query: controlledQuery,     
  onQueryChange, 
}) {
  const [internalView, setInternalView] = useState("pie");
  const view = controlledView ?? internalView;
  const [hoverIdx, setHoverIdx] = useState(null);
 const [internalQuery, setInternalQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

   const isQueryControlled = typeof onQueryChange === "function"; 
  const query = isQueryControlled ? controlledQuery : internalQuery; 
  const setQuery = isQueryControlled ? onQueryChange : setInternalQuery;

  const setView = (v) => {
    setInternalView(v);
    onViewChange?.(v);
  };

  const total = segments.reduce((s, seg) => s + seg.value, 0);

  // --- Table-mode derived data ---
  const withPercent = segments.map((seg) => ({
    ...seg,
    percent: total > 0 ? (seg.value / total) * 100 : 0,
  }));

  const filtered =
    !isQueryControlled && query
      ? withPercent.filter((s) => s.label.toLowerCase().includes(query.toLowerCase()))
      : withPercent;

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? cmp : -cmp;
      })
    : filtered;

  const toggleSort = (key) => {
    if (loading) return;
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // --- Pie-mode derived data (unchanged from DonutBreakdownCard) ---
  const strokeWidth = size * (20 / 150);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  const activeIdx = hoverIdx;
  const active = activeIdx != null ? segments[activeIdx] : null;
  const activePercent =
    active && total > 0 ? ((active.value / total) * 100).toFixed(1) : null;
  return (
    <Paper elevation={0} sx={{ p: 2.25, border: BORDER, borderRadius: 2.5, boxShadow: CARD_SHADOW }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, gap: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HEADING }}>
            {title}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {view === "table" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, border: BORDER, borderRadius: 1.5, px: 1, py: 0.5, minWidth: 160 }}>
              <SearchIcon sx={{ fontSize: 15, color: TEXT_SUBTLE }} />
              <InputBase
                placeholder="Search specialty..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
                sx={{ fontSize: 12.5, flex: 1 }}
              />
            </Box>
          )}

          <ButtonGroup size="small" sx={{ boxShadow: "none" }}>
            {["Pie", "Table"].map((label) => {
              const key = label.toLowerCase();
              return (
                <Button
                  key={key}
                  onClick={() => setView(key)}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontSize: 11.5,
                    fontWeight: 600,
                    px: 1.5,
                    color: view === key ? "#fff" : TEXT_MUTED,
                    bgcolor: view === key ? BRAND_BLUE : "transparent",
                    border: BORDER,
                    "&:hover": { bgcolor: view === key ? BRAND_BLUE : "#F1F5F9" },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>
      </Box>

      {loading ? (
        view === "pie" ? (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: 3 }}>
            <Skeleton variant="circular" width={size} height={size} sx={{ flexShrink: 0 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, width: "100%" }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", px: 1, py: 0.5 }}>
                  <Skeleton variant="text" width="55%" height={16} />
                  <Skeleton variant="text" width="15%" height={16} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box>
            {Array.from({ length: skeletonRowCount }).map((_, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #F1F5F9" }}>
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="text" width="10%" height={16} />
              </Box>
            ))}
          </Box>
        )
      ) : view === "pie" ? (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: 3 }}>
          <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                {segments.map((seg, i) => {
                  const fraction = seg.value / total;
                  const dash = fraction * circumference;
                  const gap = circumference - dash;
                  const offset = -cumulative * circumference;
                  cumulative += fraction;
                  return (
                    <circle
                      key={seg.label}
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke={seg.color}
                      strokeWidth={strokeWidth}
                      strokeDasharray={`${dash} ${gap}`}
                      strokeDashoffset={offset}
                      opacity={activeIdx == null || activeIdx === i ? 1 : 0.35}
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(null)}
                      style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                    />
                  );
                })}
              </g>
            </svg>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
              }}
            >
              {active && (
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: active.color,      
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    textAlign: "center",
                    mb: 0.25,
                  }}
                >
                  {active.label}
                </Typography>
              )}

              <Typography sx={{ fontSize: 22, fontWeight: 700, color: TEXT_HEADING, lineHeight: 1.1 }}>
                {active ? active.value.toLocaleString() : (centerLabel?.value ?? total.toLocaleString())}
              </Typography>

              {active ? (
                // NEW — percentage line, only shown while a segment is hovered
                <Typography sx={{ fontSize: 11, color: TEXT_SUBTLE, textAlign: "center", mt: 0.25 }}>
                  {activePercent}% of total
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 10, color: TEXT_SUBTLE, textAlign: "center", lineHeight: 1.3 }}>
                  {centerLabel?.label ?? "TOTAL"}
                </Typography>
              )}
            </Box>
            {/* <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: TEXT_HEADING }}>
                {active ? active.value.toLocaleString() : (centerLabel?.value ?? total.toLocaleString())}
              </Typography>
              <Typography sx={{ fontSize: 10, color: TEXT_SUBTLE, textAlign: "center", lineHeight: 1.3, px: 1 }}>
                {active ? active.label : (centerLabel?.label ?? "TOTAL")}
              </Typography>
            </Box> */}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1, width: "100%" }}>
            {segments.map((seg, i) => (
              <Box
                key={seg.label}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 0.5, borderRadius: 1, cursor: "pointer", bgcolor: activeIdx === i ? "#EFF6FF" : "transparent" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: seg.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: TEXT_HEADING }}>{seg.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TEXT_HEADING }}>
                  {seg.value.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        // Table view — matches the screenshot: Specialty | Audited Episodes | % of Total
        <>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                {[
                  { key: "label", label: "Specialty", align: "left" },
                  { key: "value", label: "Audited Episodes", align: "right" },
                  { key: "percent", label: "% of Total", align: "right" },
                ].map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    onClick={() => toggleSort(c.key)}
                    sx={{ fontSize: 10.5, fontWeight: 700, color: TEXT_SUBTLE, textTransform: "uppercase", border: "none", pb: 1, cursor: "pointer", userSelect: "none" }}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                      {c.label}
                      {sortKey === c.key ? (
                        sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((seg) => (
                <TableRow key={seg.label} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                  <TableCell sx={{ fontSize: 12.5, color: "#334155", borderColor: "#F1F5F9" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: seg.color, flexShrink: 0 }} />
                      {seg.label}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, color: "#334155", borderColor: "#F1F5F9" }}>
                    {seg.value.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, color: "#334155", borderColor: "#F1F5F9" }}>
                    {seg.percent.toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
        {(footerLeft || footerRight) && (
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5, pt: 1.5, borderTop: BORDER }}>
        <Typography sx={{ fontSize: 11.5, color: TEXT_SUBTLE }}>{footerLeft}</Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: TEXT_HEADING }}>{footerRight}</Typography>
      </Box>
    )}
    </Paper>
  );
}
/* ---------------------------------------------------------------------------
 * PaginationBar — "Rows per page: 10  ·  1-10 of 48 DRGs  ·  < 1 2 3 … 5 >"
 * ------------------------------------------------------------------------- */
export function PaginationBar({
  page,
  pageCount,
  rowsPerPage,
  rowsPerPageOptions = [5, 10, 25],
  onRowsPerPageChange,
  rangeLabel,
  onPageChange,
}) {
  const pageNumbers = (() => {
    if (pageCount <= 5)
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, "…", pageCount];
    if (page >= pageCount - 2)
      return [1, "…", pageCount - 2, pageCount - 1, pageCount];
    return [1, "…", page, "…", pageCount];
  })();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mt: 1.5,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
          Showing
        </Typography>
        {/* <FormControl size="small">
          <Select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange?.(e.target.value)}
            sx={{
              fontSize: 12,
              minWidth: 64,
              "& .MuiSelect-select": { py: 0.5 },
            }}
          >
            {rowsPerPageOptions.map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}
        {rangeLabel && (
          <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
            {rangeLabel}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          size="small"
          disabled={page === 1}
          onClick={() => onPageChange?.(page - 1)}
        >
          <Typography sx={{ fontSize: 14 }}>‹</Typography>
        </IconButton>
        {pageNumbers.map((n, i) =>
          n === "…" ? (
            <Typography
              key={`ellipsis-${i}`}
              sx={{ fontSize: 12.5, color: TEXT_SUBTLE, px: 0.5 }}
            >
              …
            </Typography>
          ) : (
            <Box
              key={n}
              onClick={() => onPageChange?.(n)}
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                bgcolor: n === page ? BRAND_BLUE : "transparent",
                color: n === page ? "#fff" : TEXT_MUTED,
              }}
            >
              {n}
            </Box>
          ),
        )}
        <IconButton
          size="small"
          disabled={page === pageCount}
          onClick={() => onPageChange?.(page + 1)}
        >
          <Typography sx={{ fontSize: 14 }}>›</Typography>
        </IconButton>
      </Box>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * PaginatedDataTableCard — DataTableCard + built-in client-side pagination.
 * Use for drill-down tables (e.g. "Cardiology DRG Breakdown") where the
 * full row set is longer than fits on screen.
 * ------------------------------------------------------------------------- */
export function PaginatedDataTableCard({
  title,
  description,
  columns = [],
  rows = [],
  itemLabel = "items",
  defaultRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25],
}) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const start = (page - 1) * rowsPerPage;
  const visibleRows = rows.slice(start, start + rowsPerPage);
  const rangeLabel =
    rows.length === 0
      ? `0 of 0 ${itemLabel}`
      : `${start + 1}-${Math.min(start + rowsPerPage, rows.length)} of ${rows.length} ${itemLabel}`;

  return (
    <DataTableCard
      title={title}
      description={description}
      columns={columns}
      rows={visibleRows}
      footer={
        <PaginationBar
          page={page}
          pageCount={pageCount}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onRowsPerPageChange={(v) => {
            setRowsPerPage(v);
            setPage(1);
          }}
          rangeLabel={rangeLabel}
          onPageChange={setPage}
        />
      }
    />
  );
}

/* Handy cell renderers for DataTableCard columns */
export function AvatarNameCell({ name, paletteIndex = 0 }) {
  const p = AVATAR_PALETTE[paletteIndex % AVATAR_PALETTE.length];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Avatar
        sx={{
          width: 22,
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: p.bg,
          color: p.color,
        }}
      >
        {name.charAt(0)}
      </Avatar>
      <Typography sx={{ fontSize: 12.5, color: "#334155" }}>{name}</Typography>
    </Box>
  );
}

export function ProgressCell({ value, color = "#10B981" }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 90 }}>
      <Typography
        sx={{ fontSize: 12.5, fontWeight: 600, color: "#334155", width: 34 }}
      >
        {value}%
      </Typography>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 10,
          bgcolor: "#EEF2F6",
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 10 },
        }}
      />
    </Box>
  );
}

export function TagCell({ label, color = BRAND_BLUE, bg = BRAND_BLUE_SOFT }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{ bgcolor: bg, color, fontWeight: 700, fontSize: 10.5, height: 20 }}
    />
  );
}

/* ---------------------------------------------------------------------------
 * KeyInsightBanner — closing callout. variant: "info" (blue) | "success" (green)
 * ------------------------------------------------------------------------- */
export function KeyInsightBanner({
  title = "Key Insight",
  text,
  variant = "success",
  icon,
}) {
  const isSuccess = variant === "success";
  const bg = isSuccess ? "#ECFDF5" : BRAND_BLUE_SOFT;
  const border = isSuccess ? "#BBF7D0" : "#DBEAFE";
  const color = isSuccess ? "#15803D" : BRAND_BLUE;
  const Icon = icon || (isSuccess ? PlaceOutlinedIcon : LightbulbOutlinedIcon);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        bgcolor: bg,
        border: `1px solid ${border}`,
        borderRadius: 1.5,
        px: 2,
        py: 1.25,
      }}
    >
      <Icon sx={{ fontSize: 18, color, mt: 0.1, flexShrink: 0 }} />
      <Box>
        <Typography
          sx={{ fontSize: 12.5, fontWeight: 700, color: TEXT_HEADING }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#334155", mt: 0.25 }}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
}
